import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminApi } from '../lib/api';
import PageHeader from '../components/PageHeader';
import usePageTitle from '../hooks/usePageTitle';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Activity, Clock, UserPlus, Heart, MessageCircle, UserMinus, AlertCircle } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { formatDistanceToNow } from 'date-fns';
import { cn } from '@/lib/utils';

/*
 * Sessions - session history and the daily performance chart, in the Aurora look (2026-08-23).
 *
 * The slot / period filters live in the page header so they stay on screen while a new period
 * loads (the body shows a skeleton instead of the whole page vanishing). Bar colours are the
 * semantic tokens so each bar matches the per-session stat of the same name below the chart;
 * likes use chart-5 (pink) rather than danger so they stay distinguishable from errors.
 */

export default function SessionsPage() {
  usePageTitle('Sessions');
  const [selectedSlot, setSelectedSlot] = useState('all');
  const [days, setDays] = useState(7);

  const { data: slots } = useQuery({
    queryKey: ['slots'],
    queryFn: () => adminApi.getSlots().then(res => res.data),
  });

  const { data: sessions, isLoading } = useQuery({
    queryKey: ['sessions', selectedSlot, days],
    queryFn: () => adminApi.getSessions({
      slot_name: selectedSlot !== 'all' ? selectedSlot : undefined,
      days,
      limit: 100,
    }).then(res => res.data),
  });

  const { data: dailyStats } = useQuery({
    queryKey: ['dailyStats', days],
    queryFn: () => adminApi.getDailyStats(days).then(res => res.data),
  });

  return (
    <div className="space-y-6" data-testid="sessions-page">
      {/* Header */}
      <PageHeader
        icon={Activity}
        title="Sessions"
        description="View session history and performance metrics"
        actions={(
          <>
            <Select value={selectedSlot} onValueChange={setSelectedSlot}>
              <SelectTrigger
                className="tap w-[180px] max-w-full bg-secondary/60 border-border text-foreground"
                aria-label="Slot"
                data-testid="sessions-slot-filter"
              >
                <SelectValue placeholder="All Slots" />
              </SelectTrigger>
              <SelectContent className="bg-popover border-border">
                <SelectItem value="all" className="text-foreground">All Slots</SelectItem>
                {slots?.map((slot) => (
                  <SelectItem key={slot.slot_id || slot.slot_name || slot.id} value={slot.slot_id || slot.slot_name} className="text-foreground">
                    {slot.slot_label || slot.slot_name} ({slot.slot_id || `slot${slot.slot_number}`})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={days.toString()} onValueChange={(v) => setDays(parseInt(v))}>
              <SelectTrigger
                className="tap w-[120px] max-w-full bg-secondary/60 border-border text-foreground"
                aria-label="Period"
                data-testid="sessions-days-filter"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-popover border-border">
                <SelectItem value="7" className="text-foreground">7 days</SelectItem>
                <SelectItem value="14" className="text-foreground">14 days</SelectItem>
                <SelectItem value="30" className="text-foreground">30 days</SelectItem>
              </SelectContent>
            </Select>
          </>
        )}
      />

      {isLoading ? (
        <div className="space-y-6" aria-busy="true" aria-label="Loading sessions" data-testid="sessions-skeleton">
          <div className="aurora-card p-5 sm:p-6">
            <div className="h-4 w-40 animate-pulse rounded-xl bg-secondary/60" />
            <div className="mt-5 h-64 animate-pulse rounded-xl bg-secondary/60 sm:h-72" />
          </div>
          <div className="aurora-card p-5 sm:p-6">
            <div className="h-4 w-48 animate-pulse rounded-xl bg-secondary/60" />
            <div className="mt-5 space-y-3">
              {[0, 1, 2].map((i) => (
                <div key={i} className="h-28 animate-pulse rounded-xl bg-secondary/60" />
              ))}
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* Stats Chart */}
          <Card className="aurora-card">
            <CardHeader className="p-5 sm:p-6">
              <CardTitle className="flex items-center gap-2 font-heading text-base font-semibold">
                <Activity className="h-5 w-5 text-primary" />
                Daily Performance
              </CardTitle>
            </CardHeader>
            <CardContent className="p-5 pt-0 sm:p-6 sm:pt-0">
              <div className="h-64 sm:h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={dailyStats || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                    <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'hsl(var(--popover))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '12px',
                        color: 'hsl(var(--foreground))',
                      }}
                    />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="follows" name="Follows" fill="hsl(var(--success))" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="likes" name="Likes" fill="hsl(var(--chart-5))" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="comments" name="Comments" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="unfollows" name="Unfollows" fill="hsl(var(--warning))" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Sessions List */}
          <Card className="aurora-card">
            <CardHeader className="p-5 sm:p-6">
              <CardTitle className="font-heading text-base font-semibold">
                Session History (<span className="tnum">{sessions?.length || 0}</span>)
              </CardTitle>
            </CardHeader>
            <CardContent className="p-5 pt-0 sm:p-6 sm:pt-0">
              {sessions && sessions.length > 0 ? (
                <div className="space-y-3">
                  {sessions.map((session, index) => {
                    const completed = session.status === 'COMPLETED';
                    return (
                      <div
                        key={session._id || index}
                        className="rounded-xl border border-border bg-secondary/60 p-4"
                        data-testid={`session-item-${index}`}
                      >
                        <div className="mb-3 flex items-center justify-between gap-3">
                          <div className="flex min-w-0 items-center gap-3">
                            <div className={cn('shrink-0 rounded-lg p-2', completed ? 'bg-success/10' : 'bg-warning/10')}>
                              <Activity className={cn('h-4 w-4', completed ? 'text-success' : 'text-warning')} />
                            </div>
                            <div className="min-w-0">
                              <p className="truncate font-medium">{session.slot_name}</p>
                              <div className="flex items-center gap-2 text-xs text-muted-foreground tnum">
                                <Clock className="h-3 w-3 shrink-0" />
                                <span className="truncate">
                                  {session.start_time ? formatDistanceToNow(new Date(session.start_time), { addSuffix: true }) : 'N/A'}
                                  {session.duration_minutes && ` • ${session.duration_minutes} min`}
                                </span>
                              </div>
                            </div>
                          </div>
                          <Badge
                            variant="outline"
                            className={cn(
                              'shrink-0 rounded-full',
                              completed ? 'border-success/30 bg-success/10 text-success' : 'border-warning/30 bg-warning/10 text-warning'
                            )}
                          >
                            {session.status}
                          </Badge>
                        </div>
                        <div className="grid grid-cols-2 gap-3 sm:grid-cols-5 sm:gap-4">
                          <div className="min-w-0 text-center">
                            <div className="flex items-center justify-center gap-1 text-success">
                              <UserPlus className="h-4 w-4 shrink-0" />
                              <span className="font-semibold tnum">{session.stats?.follows_done || 0}</span>
                            </div>
                            <p className="truncate text-xs text-muted-foreground">Follows</p>
                          </div>
                          <div className="min-w-0 text-center">
                            <div className="flex items-center justify-center gap-1 text-chart-5">
                              <Heart className="h-4 w-4 shrink-0" />
                              <span className="font-semibold tnum">{session.stats?.likes_done || 0}</span>
                            </div>
                            <p className="truncate text-xs text-muted-foreground">Likes</p>
                          </div>
                          <div className="min-w-0 text-center">
                            <div className="flex items-center justify-center gap-1 text-primary">
                              <MessageCircle className="h-4 w-4 shrink-0" />
                              <span className="font-semibold tnum">{session.stats?.comments_done || 0}</span>
                            </div>
                            <p className="truncate text-xs text-muted-foreground">Comments</p>
                          </div>
                          <div className="min-w-0 text-center">
                            <div className="flex items-center justify-center gap-1 text-warning">
                              <UserMinus className="h-4 w-4 shrink-0" />
                              <span className="font-semibold tnum">{session.stats?.unfollows_done || 0}</span>
                            </div>
                            <p className="truncate text-xs text-muted-foreground">Unfollows</p>
                          </div>
                          <div className="min-w-0 text-center">
                            <div className="flex items-center justify-center gap-1 text-danger">
                              <AlertCircle className="h-4 w-4 shrink-0" />
                              <span className="font-semibold tnum">{session.stats?.errors_count || 0}</span>
                            </div>
                            <p className="truncate text-xs text-muted-foreground">Errors</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="aurora-card aurora-card-dashed p-8 text-center sm:p-10" data-testid="sessions-empty">
                  <span className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary ring-1 ring-primary/20">
                    <Activity className="h-6 w-6" />
                  </span>
                  <p className="mt-4 text-sm text-muted-foreground">No sessions found for the selected period.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
